import React, { useMemo } from 'react';

import { Select, Space, Tooltip } from 'antd';
import Icon from '@ant-design/icons';

import { getTextWidth } from '../../hooks/utils';
import { Pill } from '../Pill';
import { ReactComponent as SxEdit } from '../../../../assets/sx-edit-uncolored.svg';

import styles from './index.module.scss';

interface IOptionsBase {
  label: string;
  value: string;
  disabled?: boolean;
  onClose?: () => void;
  closable?: boolean;
}

export interface IPerson {
  firstName: string;
  lastName: string;
  email: string;
}

interface IOptions extends IOptionsBase {
  data: IPerson;
}

const SPACING_ACCOUNTING_FOR_AVATAR_IN_PX = 40;
const ELLIPSIS_WIDTH_IN_PX = 43;
const PENCIL_WIDTH_PX = 30;
const TAGS_COLUMN_GAP_IN_PX = 8;

interface PillsGroupProps {
  users: ReadonlyArray<IPerson>;
  intendedNumberOfLines: number;
  width: number;
  onEdit?: () => void;
}

class SpaceAllocator { // TODO: tests
  private readonly widthAvailablePerRowInPx: number[];
  private rowIndex: number;
  private itemsToPutAggregateInEllipsis: number;
  private hasFinishedAllocation = false;
  private spaceForEllipsisAndEdit: number;

  private usage: number[][] = [];

  constructor(
    private readonly rowsCount: number,
    private readonly availableWidthInPx: number,
    includesEditCta: boolean
  ) {
    this.widthAvailablePerRowInPx = Array.from({ length: rowsCount }, (_,index) => {
      return availableWidthInPx;
    });
    this.rowIndex = 0;
    this.itemsToPutAggregateInEllipsis = 0;
    this.usage = Array.from({ length: rowsCount }, () => { return []; });
    this.spaceForEllipsisAndEdit = includesEditCta ? (ELLIPSIS_WIDTH_IN_PX + PENCIL_WIDTH_PX + TAGS_COLUMN_GAP_IN_PX) : (ELLIPSIS_WIDTH_IN_PX + TAGS_COLUMN_GAP_IN_PX);
  }

  public allocate(itemWidthInPx: number) {
    if(this.willTheItemFitInTheCurrentRow(itemWidthInPx) && !this.hasFinishedAllocation) {
      this.allocateSpaceInCurrentRow(itemWidthInPx);
    } else if(this.isThereNextRow()) {
      if(this.willTheItemFitInTheNextRow(itemWidthInPx)) {
        this.rowIndex++;
        this.allocate(itemWidthInPx);
      } else {
        this.itemsToPutAggregateInEllipsis++;
        if(this.willTheItemFitInTheCurrentRow(this.spaceForEllipsisAndEdit)) {
          this.allocateSpaceForEllipsisAndEdit();
        } else if(this.willTheItemFitInTheNextRow(this.spaceForEllipsisAndEdit)) {
          this.rowIndex++;
          this.allocateSpaceForEllipsisAndEdit();
        }
      }
    } else {
      this.itemsToPutAggregateInEllipsis++;
      if(!this.hasFinishedAllocation) {
        this.allocateSpaceForEllipsisAndEdit();
        return;
      }
    }
  }

  public canAllocate(itemWidthInPx: number) {
    if(this.hasFinishedAllocation) {
      return false;
    }
    if(this.willTheItemFitInTheCurrentRow(itemWidthInPx)) {
      return true;
    }
    return this.isThereNextRow() && this.willTheItemFitInTheNextRow(itemWidthInPx);
  }
  private allocateSpaceForEllipsisAndEdit() {
    if(this.willTheItemFitInTheCurrentRow(this.spaceForEllipsisAndEdit)) {
      this.allocate(this.spaceForEllipsisAndEdit);
    } else {
      const valueToRelease = this.usage[this.rowIndex].slice(-1)[0] || 0;
      this.deallocateSpaceInCurrentRow(valueToRelease);
      this.allocateSpaceInCurrentRow(this.spaceForEllipsisAndEdit);
      this.itemsToPutAggregateInEllipsis++;
    }
    this.hasFinishedAllocation = true;
  }

  private isThereNextRow() {
    return (this.rowsCount - 1) > this.rowIndex;
  }

  private willTheItemFitInTheNextRow(itemWidthInPx: number) {
    return this.availableWidthInPx > itemWidthInPx;
  }

  private willTheItemFitInTheCurrentRow(itemWidthInPx: number) {
    const currentRowAvailability = this.widthAvailablePerRowInPx[this.rowIndex];
    return currentRowAvailability > itemWidthInPx;
  }

  private allocateSpaceInCurrentRow(itemWidthInPx: number) {
    this.widthAvailablePerRowInPx[this.rowIndex] -= itemWidthInPx;
    this.usage[this.rowIndex].push(itemWidthInPx);
  }

  private deallocateSpaceInCurrentRow(itemWidthInPx: number) {
    this.widthAvailablePerRowInPx[this.rowIndex] -= itemWidthInPx;
    this.usage[this.rowIndex].pop();
  }

  getCountOfItemsToHide(): number {
    return this.itemsToPutAggregateInEllipsis;
  }
}

/**
 * @description This component does its best when dealing with the last line, this is so because the maxTagPlaceholder returned ReactNode value
 * is rendered inside a single ant-select-selection-overflow-item-rest element, which may include a 'large item' + the ellipsis + the optional edit icon
 * then <Select /> element may decide to render it in a new line (render is out of our control), causing a mismatch with intendedNumberOfLines
 * Behaves better with smaller Pills
 */
const PillsGroup: React.FC<PillsGroupProps> = ({ users, intendedNumberOfLines, width, onEdit }) => {
  const options: IOptions[] = [];
  const selection: string[] = [];

  const isEditable = !!onEdit;
  const handleEdit = onEdit ?? (() => true);

  const labelsWidth = useMemo(() => {
    const widths: number[] = [];
    let index = 0;
    for (const user of users) {
      widths[index++] = getTextWidth(`${user.firstName} ${user.lastName}`, { size: '12px', family: 'Source Sans Pro', weight: 'normal' });
    }
    return widths;
  }, [users]);

  const allocator = new SpaceAllocator(intendedNumberOfLines, width, isEditable);

  users.forEach((user, userIndex) => {
    const label = `${user.firstName.concat(' ').concat(user.lastName)}`;
    const labelWidth = labelsWidth[userIndex];

    options.push({
      label,
      value: user.email,
      data: user
    });
    selection.push(user.email);
    const extraForEditAndEllipsis = (userIndex >= (users.length - 1)) ? PENCIL_WIDTH_PX : 0;
    const canAllocate = allocator.canAllocate(labelWidth + SPACING_ACCOUNTING_FOR_AVATAR_IN_PX + TAGS_COLUMN_GAP_IN_PX + PENCIL_WIDTH_PX);
    if(canAllocate) {
      allocator.allocate(labelWidth + SPACING_ACCOUNTING_FOR_AVATAR_IN_PX + TAGS_COLUMN_GAP_IN_PX);
    } else {
      allocator.allocate(labelWidth + SPACING_ACCOUNTING_FOR_AVATAR_IN_PX + TAGS_COLUMN_GAP_IN_PX + extraForEditAndEllipsis);
    }
  });

  const itemsToHide = allocator.getCountOfItemsToHide();
  const totalToHide = itemsToHide === 0 ? 1 : itemsToHide; // hide at least one so edit button can be rendered

  if(users.length === 0) {
    return <Space direction="vertical" className={styles.pillsGroup} />;
  }

  return (
    <Space direction="vertical" className={styles.pillsGroup}>
      <Select
        mode='multiple'
        className={styles.select}
        value={selection}
        options={options}
        placeholder={null}
        maxTagCount={options.length - totalToHide}
        autoFocus={false}
        removeIcon={null}
        disabled
        tagRender={(item) => {
          const { value } = item;
          const optionFound = options.find((option) => {
            return option.value === value;
          });

          if(optionFound) {
            return (
              <Pill
                firstName={optionFound.data.firstName}
                lastName={optionFound.data.lastName}
              />
            );
          }

          return <></>;
        }}
        bordered={false}
        allowClear={false}
        maxTagPlaceholder={(ommitedValues) => {
          const tooltipDescription = ommitedValues.map((ommited) => ommited.label).join(', ');
          return (
            <>
              {
                (itemsToHide === 0) ? (
                  <Pill
                    className={styles.pill}
                    firstName={ommitedValues[0]?.label?.toString().split(' ')[0] || ''} // Needed to split as values do not come separated
                    lastName={ommitedValues[0]?.label?.toString().split(' ')[1] || ''}
                  />
                ) : null
              }
              {
                ((ommitedValues.length - (itemsToHide === 0 ? 1 : 0)) === 0) ? null : (
                  <Tooltip title={tooltipDescription} placement='bottom' className={styles.ommitedValues}>
                    + {ommitedValues.length}...
                  </Tooltip>
                )
              }
              {
                isEditable ? (
                  <Tooltip title='Edit users' placement='bottom' className={styles.edit}>
                    <Icon
                      component={SxEdit}
                      onClick={handleEdit}
                    />
                  </Tooltip>
                ) : null
              }
            </>
          );
        }}
      />
    </Space>
  );
};

export { PillsGroup };
